import API_BASE_URL from '../config';
import { getAuthHeaders } from '../utils/authHeaders';

export type MediaType = 'image' | 'video';

export interface CreateMediaUploadResponse {
  uploadUrl: string; // SAS URL
  blobUrl: string;   // plain blob URL (stored in DB / displayed)
  mediaType: MediaType;
  maxBytes?: number;
}

export interface UploadProgress {
  loadedBytes: number;
  totalBytes: number;
  percent: number; // 0..100
}

export async function createMediaUpload(file: File): Promise<CreateMediaUploadResponse> {
  const headers = getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/api/createMediaUpload`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      contentType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
    }),
  });

  if (!resp.ok) {
    let details: any = null;
    try {
      details = await resp.json();
    } catch {
      // ignore
    }
    const msg = details?.error?.message || `Failed to create upload URL (HTTP ${resp.status})`;
    throw new Error(msg);
  }

  return (await resp.json()) as CreateMediaUploadResponse;
}

export async function uploadFileToBlob(params: {
  uploadUrl: string;
  file: File;
  onProgress?: (p: UploadProgress) => void;
}): Promise<void> {
  const { uploadUrl, file, onProgress } = params;
  const totalBytes = file.size;

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    if (onProgress) {
      xhr.upload.onprogress = (ev: ProgressEvent) => {
        if (!ev.lengthComputable) return;
        const loadedBytes = ev.loaded;
        const percent = totalBytes > 0 ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100)) : 0;
        onProgress({ loadedBytes, totalBytes, percent });
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with HTTP ${xhr.status}: ${xhr.statusText}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Network error during upload'));
    };

    xhr.onabort = () => {
      reject(new Error('Upload aborted'));
    };

    xhr.send(file);
  });
}

export async function uploadFileViaSas(params: {
  file: File;
  onProgress?: (p: UploadProgress) => void;
}): Promise<{ blobUrl: string; mediaType: MediaType }> {
  const { file, onProgress } = params;

  const { uploadUrl, blobUrl, mediaType } = await createMediaUpload(file);
  await uploadFileToBlob({ uploadUrl, file, onProgress });
  return { blobUrl, mediaType };
}

